import {
  Button,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  CreateBudgetovacPositionModal,
  UpdateBudgetovacPositionModal,
} from "@src/components/modules/budgets/positions/components";
import {
  type BudgetovacPositionTableRow,
  BudgetPositionsStore,
} from "@src/components/modules/budgets/positions/stores";
import {
  ACTIONS_COLUMN_ID,
  DataTable,
  LoadingOverlay,
  ModalConfirm,
} from "@src/components/ui-kit";
import type { BodyColumnDef } from "@src/components/ui-kit/DataTable/types";
import { Icon } from "@src/components/ui-kit/Icon";
import { withCan } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { observer, useLocalObservable } from "mobx-react-lite";
import { createContext, useContext } from "react";

export const BudgetovacPositionsContext = createContext(
  {} as BudgetPositionsStore,
);

export const BudgetPositionsPage = withCan(
  { do: "integration_budgetovac" },
  observer(function BudgetPositionsPage() {
    const appStore = useStore();
    const { store, columns } = useLocalObservable(() => ({
      store: new BudgetPositionsStore(appStore),
      columns: createColumns(),
    }));

    return (
      <BudgetovacPositionsContext.Provider value={store}>
        <DataTable>
          <DataTable.NavBar>
            <HStack justify="flex-end" py="3">
              <Button
                leftIcon={<Icon name="plus" />}
                onClick={store.createStore.disclosure.onOpen}
                variant="outline"
              >
                <Trans>New position</Trans>
              </Button>
            </HStack>
          </DataTable.NavBar>

          <LoadingOverlay
            isLoading={store.isLoadingPositions}
            position="absolute"
            size="md"
          >
            <DataTable.Body
              tableKey={store.tableKey}
              columns={columns}
              data={store.positions}
            />
          </LoadingOverlay>
        </DataTable>

        <CreateBudgetovacPositionModal store={store.createStore} />
        <UpdateBudgetovacPositionModal store={store.updateStore} />

        <ModalConfirm
          destructive
          isOpen={store.deleteModalDisclosure.isOpen}
          onClose={store.deleteModalDisclosure.onClose}
          onConfirm={store.deletePosition}
          isLoading={store.isDeleting}
          message={t`Are you sure you want to delete this position?`}
          confirmBtnTitle={t`Delete`}
          heading={`${t`Delete position`} ${store.getPositionToDelete?.name}`}
        />
      </BudgetovacPositionsContext.Provider>
    );
  }),
);

function createColumns(): BodyColumnDef<BudgetovacPositionTableRow>[] {
  return [
    {
      id: "name",
      header: t`Name`,
      cell: ({ row }) => row.original.name,
    },
    {
      id: "department",
      header: t`Department`,
      cell: ({ row }) => row.original.departmentName,
    },
    {
      id: ACTIONS_COLUMN_ID,
      cell: ({ row }) => {
        const store = useContext(BudgetovacPositionsContext);

        return (
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<Icon name="dots-vertical" />}
              variant="ghost"
            />
            <MenuList>
              <MenuItem
                icon={<Icon name="edit-02" />}
                onClick={() => {
                  store.updateStore.disclosure.onOpen({
                    position: row.original,
                  });
                }}
              >
                <Trans>Edit</Trans>
              </MenuItem>
              <MenuItem
                icon={<Icon color="red.500" name="trash-03" />}
                onClick={() => {
                  store.deleteModalDisclosure.onOpen({
                    id: row.original.id,
                  });
                }}
              >
                <Trans>Delete</Trans>
              </MenuItem>
            </MenuList>
          </Menu>
        );
      },
    },
  ];
}
